/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import Hero from '@/components/hero';
// Vite asset import (not a raw `/images/...` path, which 404s on Managed Runtime). 2048px-wide
// JPEG exported from the Figma "P1_Hero" frame (DesignSystem node 12066-1170 / mobile 12066-1174).
import heroFallbackImage from '/images/hero-fallback.jpg';

/**
 * Static fallback content for the Hero — the hardcoded backup shown when the `homepage` Page
 * Designer hero region is empty or fails to load, so the hero area is never blank/broken before
 * a merchandiser authors it. Content mirrors the "P1_Hero" design frame: light ripper-paper tone,
 * "Don't just plant." headline, and a Soil Green "grow." highlight over the photo.
 */
export default function HeroFallback(): ReactElement {
    return (
        <Hero
            title="Don’t just plant."
            mediaType="image"
            image={{
                fileURL: heroFallbackImage,
                alternativeText: 'A gardener pouring Black Magic soil from a bag into a planter on a deck.',
            }}
            subheader="grow."
            highlight={{ backgroundColor: 'green', textColor: 'dark' }}
            ripperTone="light"
        />
    );
}
